import { createFileRoute, Link } from '@tanstack/react-router';
import { ImagesIcon, MapTrifoldIcon } from '@phosphor-icons/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';

import { EmptyState } from '@/components/empty-state';
import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { BackupReminderBanner } from '@/features/backup';
import { BulkExportComposer } from '@/features/export-image';
import {
  FlightList,
  FlightListControls,
  FlightsOverviewMap,
  isLogbookFilter,
  isSortId,
  isSourceFilter,
  type LogbookFilter,
  matchesLogbook,
  matchesSummaryQuery,
  matchesSummarySource,
  type SortId,
  type SourceFilter,
  useFlightSummaries,
} from '@/features/flights';
import { DEFAULT_SETTINGS, useSettings } from '@/features/settings';
import { ImportButton } from '@/features/import';
import type { Track, TrackSourceKind } from '@/lib/db';
import { tracks } from '@/lib/db';
import { formatCount } from '@/lib/format';

/** Every `TrackSourceKind`, for validating a `source` search param against something other than a guess. */
const TRACK_SOURCES: readonly TrackSourceKind[] = [
  'fr24-kml',
  'fr24-csv',
  'gpx',
  'igc',
  'geojson',
  'fr24-api',
  'opensky',
  'adsb',
];

export interface FlightsSearch {
  q?: string;
  source?: SourceFilter;
  sort?: SortId;
  logbook?: LogbookFilter;
}

/**
 * The batch image export, fed the tracks the list is showing.
 *
 * Unfiltered, that is every stored track, read straight from the table —
 * the flight list only joins its most recent 500, and "export all" should
 * not quietly stop there. Filtered, it is the tracks behind the summaries
 * that match, the same predicates `FlightList` applies, so the zip holds
 * exactly the flights on screen. An entry typed by hand has no track and
 * nothing to draw, so it is left out either way.
 */
function FlightImagesExport({
  query,
  source,
  logbook,
  onClose,
}: {
  query: string;
  source: SourceFilter;
  logbook: LogbookFilter;
  onClose: () => void;
}) {
  const settings = useSettings() ?? DEFAULT_SETTINGS;
  const filtered = query !== '' || source !== 'all' || logbook !== 'all';
  const allTracks = useLiveQuery(() => (filtered ? [] : tracks.list()), [filtered], null);
  const summaries = useFlightSummaries();

  let selected: Track[] | null;
  if (!filtered) {
    selected = allTracks;
  } else if (summaries === null) {
    selected = null;
  } else {
    const byId = new Map<string, Track>();
    for (const summary of summaries) {
      if (
        summary.track !== undefined &&
        matchesSummaryQuery(summary, query) &&
        matchesSummarySource(summary, source) &&
        matchesLogbook(summary, logbook)
      ) {
        byId.set(summary.track.id, summary.track);
      }
    }
    selected = [...byId.values()];
  }

  if (selected === null) {
    return null;
  }

  return (
    <BulkExportComposer
      tracks={selected}
      onClose={onClose}
      defaultOptions={{
        paletteId: settings.defaultPaletteId,
        units: {
          altitude: settings.altitudeUnit,
          speed: settings.speedUnit,
          distance: settings.distanceUnit,
        },
        dateFormat: settings.dateFormat,
        timeZone: settings.timeZone,
      }}
    />
  );
}

function FlightsPage() {
  // `null` while the database is opening, which is a different thing from zero
  // tracks and must not render as "no flights yet" on the way past.
  const trackCount = useLiveQuery(() => tracks.count(), [], null);
  const [exportingImages, setExportingImages] = useState(false);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const query = search.q ?? '';
  const source = search.source ?? 'all';
  const sort = search.sort ?? 'recent';
  const logbook = search.logbook ?? 'all';

  /*
   * Builds the *whole* next search object rather than patching the current
   * one — a cleared field needs to disappear from the URL, not persist as
   * whatever it last was, and a plain `{ ...prev, ...patch }` merge can only
   * ever add or overwrite a key, never drop one back to its default.
   */
  const buildSearch = (next: {
    q?: string;
    source?: SourceFilter;
    sort?: SortId;
    logbook?: LogbookFilter;
  }): FlightsSearch => {
    const nextQuery = next.q ?? query;
    const nextSource = next.source ?? source;
    const nextSort = next.sort ?? sort;
    const nextLogbook = next.logbook ?? logbook;
    return {
      ...(nextQuery === '' ? {} : { q: nextQuery }),
      ...(nextSource === 'all' ? {} : { source: nextSource }),
      ...(nextSort === 'recent' ? {} : { sort: nextSort }),
      ...(nextLogbook === 'all' ? {} : { logbook: nextLogbook }),
    };
  };

  if (trackCount === null) {
    return (
      <div className="mx-auto w-full max-w-xl">
        <p className="text-muted-foreground text-xs">
          <span
            aria-hidden="true"
            className="bg-surface-sunken rounded-control inline-block h-3 w-40 align-middle"
          />
          <span className="sr-only">Opening the local database</span>
        </p>
      </div>
    );
  }

  if (trackCount === 0) {
    return (
      <div className="mx-auto w-full max-w-xl">
        <EmptyState
          icon={MapTrifoldIcon}
          title="No flights"
          description="Import or drop FR24 KML, KMZ or CSV files, or GPX or IGC logs."
        >
          <div className="flex flex-col items-center gap-2">
            <ImportButton />
            {/*
              Second, and quieter, deliberately: a file is complete from
              engine-on and costs nothing, and an API track is the fallback for
              when there isn't one. `PLAN.md` §4 — file import is the v1 path.
            */}
            <Link
              to="/flights/fetch"
              className="text-muted-foreground hover:text-foreground text-sm underline underline-offset-2"
            >
              Fetch from an API
            </Link>
            {/*
              A second device starts here too, empty, with its flights already
              in a sync folder somewhere. One quiet link, so it does not have
              to find that out from Settings.
            */}
            <Link
              to="/settings"
              hash="sync"
              className="text-muted-foreground hover:text-foreground text-sm underline underline-offset-2"
            >
              Sync from another device
            </Link>
          </div>
        </EmptyState>
      </div>
    );
  }

  /*
   * Full width, where the list used to sit in a 768px column in the middle of
   * a 1440px screen. The map is the reason to open this page, and a wider
   * one shows more of the country at the same zoom.
   */
  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h1 className="text-lg font-semibold tracking-tight">Flights</h1>
        <div className="flex items-center gap-3">
          <p className="text-muted-foreground text-xs" data-testid="storage-status">
            <span className="tabular">{formatCount(trackCount)}</span>{' '}
            {trackCount === 1 ? 'track' : 'tracks'} stored.
          </p>
          <Button
            variant="outline"
            size="sm"
            aria-pressed={exportingImages}
            onClick={() => {
              setExportingImages((open) => !open);
            }}
          >
            <ImagesIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
            Export images
          </Button>
        </div>
      </div>
      <BackupReminderBanner />
      {exportingImages ? (
        <FlightImagesExport
          query={query}
          source={source}
          logbook={logbook}
          onClose={() => {
            setExportingImages(false);
          }}
        />
      ) : null}
      {/*
        The map before the controls, because it is the thing the page is for:
        a pilot opening "Flights" wants to see where they have been, and the
        search below narrows it exactly as it narrows the list.
      */}
      <FlightsOverviewMap query={query} source={source} logbook={logbook} />
      <FlightListControls
        query={query}
        onQueryChange={(value) => {
          void navigate({ search: buildSearch({ q: value }), replace: true });
        }}
        source={source}
        onSourceChange={(value) => {
          void navigate({ search: buildSearch({ source: value }), replace: true });
        }}
        sort={sort}
        onSortChange={(value) => {
          void navigate({ search: buildSearch({ sort: value }), replace: true });
        }}
        logbook={logbook}
        onLogbookChange={(value) => {
          void navigate({ search: buildSearch({ logbook: value }), replace: true });
        }}
      />
      <FlightList query={query} source={source} sort={sort} logbook={logbook} />
    </div>
  );
}

export const Route = createFileRoute('/flights/')({
  validateSearch: (search: Record<string, unknown>): FlightsSearch => {
    const q = typeof search.q === 'string' && search.q !== '' ? search.q : undefined;
    const source =
      typeof search.source === 'string' && isSourceFilter(search.source, TRACK_SOURCES)
        ? (search.source as SourceFilter)
        : undefined;
    const sort = typeof search.sort === 'string' && isSortId(search.sort) ? search.sort : undefined;
    const logbook =
      typeof search.logbook === 'string' && isLogbookFilter(search.logbook)
        ? search.logbook
        : undefined;
    return {
      ...(q === undefined ? {} : { q }),
      ...(source === undefined ? {} : { source }),
      ...(sort === undefined ? {} : { sort }),
      ...(logbook === undefined ? {} : { logbook }),
    };
  },
  component: FlightsPage,
});
